import { useQuery } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import { api } from "../../lib/api";
import { formatMoney, titleCase } from "../../lib/format";
import { PageHeader, Sheet, SheetHeader, Skeleton } from "../../components/ui";
import { StatTile } from "../../components/charts";

interface AdminOverviewResponse {
  tenants: Record<string, number>;
  pending_kyc_reviews: number;
  platform_fee_revenue: { currency: string; minor: number; formatted: string }[] | null;
  dead_jobs: number;
}

export function AdminOverview() {
  const overview = useQuery({
    queryKey: ["admin-overview"],
    queryFn: () => api.get<AdminOverviewResponse>("/api/admin/overview"),
    refetchInterval: 30_000,
  });

  if (overview.isLoading) {
    return (
      <Sheet>
        <Skeleton rows={6} />
      </Sheet>
    );
  }

  const data = overview.data!;
  const revenue = data.platform_fee_revenue ?? [];
  const usd = revenue.find((r) => r.currency === "USD");
  const tenants = data.tenants ?? {};
  const totalTenants = Object.values(tenants).reduce((sum, n) => sum + n, 0);

  return (
    <>
      <PageHeader
        eyebrow="Operations"
        title="Platform overview"
        description="How PayFlow itself is doing: client firms, revenue earned, and anything waiting on a human."
      />

      <div className="settle settle-1 mb-6 grid grid-cols-1 gap-px overflow-hidden rounded-lg border border-rule bg-rule sm:grid-cols-2 lg:grid-cols-4">
        <div className="bg-paper-raised">
          <StatTile
            label="Fee revenue"
            value={usd ? formatMoney(usd.minor, "USD") : "USD 0.00"}
            detail="Earned to date"
            accent="brass"
          />
        </div>
        <div className="bg-paper-raised">
          <StatTile
            label="Approved firms"
            value={String(tenants.approved ?? 0)}
            detail={`${totalTenants} registered in total`}
            accent="verdigris"
          />
        </div>
        <div className="bg-paper-raised">
          <StatTile
            label="Awaiting review"
            value={String(data.pending_kyc_reviews)}
            detail="Verification applications"
          />
        </div>
        <div className="bg-paper-raised">
          <StatTile
            label="Failed jobs"
            value={String(data.dead_jobs)}
            detail={data.dead_jobs > 0 ? "Needs attention" : "Nothing stuck"}
          />
        </div>
      </div>

      {data.pending_kyc_reviews > 0 && (
        <Link
          to="/admin/verification"
          className="settle settle-2 mb-6 flex items-center justify-between gap-4 rounded-lg border border-brass/30 bg-brass-wash px-5 py-4 transition-colors hover:bg-brass-wash/70"
        >
          <div>
            <p className="text-[13.5px] font-semibold text-ink">
              {data.pending_kyc_reviews} {data.pending_kyc_reviews === 1 ? "firm is" : "firms are"} waiting
              on verification
            </p>
            <p className="mt-0.5 text-[12.5px] text-ink-muted">
              They cannot take live payments until someone reviews their documents.
            </p>
          </div>
          <span className="shrink-0 text-[12.5px] font-medium text-ink">Open the queue →</span>
        </Link>
      )}

      {data.dead_jobs > 0 && (
        <div className="settle settle-2 mb-6 rounded-lg border border-sienna/30 bg-sienna-wash px-5 py-4">
          <p className="text-[13.5px] font-semibold text-sienna">
            {data.dead_jobs} background {data.dead_jobs === 1 ? "job has" : "jobs have"} exhausted their
            retries
          </p>
          <p className="mt-0.5 text-[12.5px] text-ink-muted">
            These are usually webhook deliveries to unreachable merchant endpoints. Check the worker logs.
          </p>
        </div>
      )}

      <div className="grid gap-6 lg:grid-cols-2">
        <Sheet className="settle settle-3 overflow-hidden">
          <SheetHeader title="Client firms by status" />
          <ul className="divide-y divide-rule">
            {Object.entries(tenants)
              .sort(([, a], [, b]) => b - a)
              .map(([status, count]) => (
                <li key={status} className="flex items-baseline justify-between px-5 py-2.5">
                  <span className="text-[13px] text-ink-muted">{titleCase(status)}</span>
                  <span className="font-mono text-[14px] tabular">{count}</span>
                </li>
              ))}
            {Object.keys(tenants).length === 0 && (
              <li className="px-5 py-6 text-center text-[13px] text-ink-muted">
                No firms registered yet.
              </li>
            )}
          </ul>
        </Sheet>

        <Sheet className="settle settle-4 overflow-hidden">
          <SheetHeader title="Fee revenue by currency" />
          {revenue.length === 0 ? (
            <p className="px-5 py-8 text-center text-[13px] text-ink-muted">
              No revenue recorded yet.
            </p>
          ) : (
            <ul className="divide-y divide-rule">
              {revenue.map((row) => (
                <li key={row.currency} className="flex items-baseline justify-between px-5 py-3.5">
                  <span className="font-mono text-[12px] text-ink-muted">{row.currency}</span>
                  <span className="font-display text-[22px] tabular text-brass">
                    {row.formatted}
                  </span>
                </li>
              ))}
            </ul>
          )}
          <div className="border-t border-rule px-5 py-3">
            <Link to="/admin/ledger" className="text-[12.5px] text-verdigris underline underline-offset-2">
              Open the ledger
            </Link>
          </div>
        </Sheet>
      </div>
    </>
  );
}
